// client/src/pages/MyPayments.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { apiJson, socket, fetchMe } from '../lib/api';
import { fmtGroup, fmtLocal, tzLabel } from '../lib/dates';
import { FaRegCopy } from 'react-icons/fa';
import logo from '../assets/chitconnect-logo-blue.png';
import '../App.css';
import { formatMoneyForGroup } from '../lib/money';

export default function MyPayments({ user }) {
  const { groupId: routeGroupId } = useParams();
  const location = useLocation();
  // Some routes render this page without passing `user`.
  // Fall back to session/local storage so PayTo lookups still work.
  const sessionUser = useMemo(() => {
    if (user && typeof user === 'object') return user;
    try {
      const raw = sessionStorage.getItem('user') || localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [user]);

  const [me, setMe] = useState(sessionUser);

// Keep `me` in sync with storage/prop user when available
useEffect(() => {
  if (sessionUser?.id) setMe(sessionUser);
}, [sessionUser?.id]);

// If we still don't have an id (some routes don't pass user and storage may be empty),
// fetch /api/me so PayTo lookups always run.
useEffect(() => {
  let alive = true;
  if (me?.id) return;
  (async () => {
    try {
      const u = await fetchMe();
      if (alive && u?.id) setMe(u);
    } catch {
      // ignore
    }
  })();
  return () => { alive = false; };
}, [me?.id]);

const myUserId = me?.id != null ? Number(me.id) : null;

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ paid: 0, pending: 0, late: 0 });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);

  // If navigated here from a notification like /payments?groupId=...&period=...
  // or legacy /groups/:groupId/payments, preselect that group once.
  const preferredGroupId = useMemo(() => {
    try {
      const qs = new URLSearchParams(location.search || '');
      return (
        qs.get('groupId') ||
        qs.get('group_id') ||
        qs.get('group') ||
        routeGroupId ||
        ''
      );
    } catch {
      return routeGroupId || '';
    }
  }, [location.search, routeGroupId]);

  useEffect(() => {
    if (selectedGroup) return;
    if (!preferredGroupId) return;
    if (!Array.isArray(groups) || groups.length === 0) return;
    const exists = groups.some((g) => String(g?.id) === String(preferredGroupId));
    if (exists) setSelectedGroup(String(preferredGroupId));
  }, [preferredGroupId, groups, selectedGroup]);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  // Group payment rows by period so users with multiple memberships
  // (including organisers) don't see duplicate cards/rows.
  const periodGroups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const p = Number(r.period_number);
      if (!p) continue;
      if (!map.has(p)) map.set(p, []);
      map.get(p).push(r);
    }

    for (const arr of map.values()) {
      arr.sort((a, b) => Number(a.membership_index || 1) - Number(b.membership_index || 1));
    }

    return Array.from(map.entries())
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([period, items]) => ({ period: Number(period), items }));
  }, [rows]);

  // ---- responsive listener ----
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---- toast helper (memoized) ----
  const hideTimerRef = useRef(null);
  const show = useCallback((msg, ms = 3000) => {
    setMessage(msg);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (ms) hideTimerRef.current = setTimeout(() => setMessage(''), ms);
  }, []);
  useEffect(() => () => hideTimerRef.current && clearTimeout(hideTimerRef.current), []);

  const selectedGroupObj = useMemo(() => {
    return groups.find((g) => String(g.id) === String(selectedGroup)) || null;
  }, [groups, selectedGroup]);

  const groupZone = useMemo(() => {
    return (
      selectedGroupObj?.timezone ||
      selectedGroupObj?.auction_timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  }, [selectedGroupObj]);

  const money = useCallback(
    (v, opts) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return '—';
      return formatMoneyForGroup(n, selectedGroupObj, opts);
    },
    [selectedGroupObj]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Clipboard helpers (mobile-safe). We never display/copy ciphertext (enc:...).
  // ─────────────────────────────────────────────────────────────────────────────
  const isEncryptedValue = useCallback((v) => typeof v === 'string' && v.startsWith('enc:'), []);

  // Never show full bank details on screen. Show only the last digits.
  // Full values remain copyable via the copy button.
  const maskBankValue = useCallback((v, visible = 3) => {
    if (!v) return '—';
    const s = String(v);
    if (s.startsWith('enc:')) return 'Encrypted';

    // Extract digits only (BSB/account are numeric). If it isn't numeric, fall back.
    const digits = s.replace(/\D/g, '');
    if (!digits) return s;

    const vis = Math.max(0, Math.min(Number(visible) || 0, digits.length));
    const tail = digits.slice(-vis);
    const masked = '*'.repeat(Math.max(0, digits.length - vis)) + tail;
    return masked;
  }, []);

  const displayBankValue = useCallback(
    (v, field) => {
      // Show last 2 digits for BSB, last 3 digits for account number by default.
      const visible = field === 'bsb' ? 2 : 3;
      return maskBankValue(v, visible);
    },
    [maskBankValue]
  );

  const copyText = useCallback(async (text) => {
    const t = String(text || '').trim();
    if (!t) return false;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(t);
        return true;
      }
    } catch {
      // fall through
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = t;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return !!ok;
    } catch {
      return false;
    }
  }, []);

  const copyPayoutField = useCallback(
    async (payout, field) => {
      try {
        if (!payout) throw new Error('Payout not set yet');

        const raw =
          field === 'name'
            ? payout.account_holder
            : field === 'bsb'
            ? payout.bsb
            : payout.account_number;

        const val = String(raw || '').trim();
        if (!val) throw new Error('Nothing to copy');

        if (isEncryptedValue(val)) {
          throw new Error('Cannot copy encrypted payout details. Fix ACCOUNT_ENC_KEY on UAT.');
        }

        const ok = await copyText(val);
        if (!ok) throw new Error('Copy failed');

        show('Copied');
      } catch (e) {
        show(e?.message || 'Copy failed');
      }
    },
    [copyText, isEncryptedValue, show]
  );

  // Load user's groups
  useEffect(() => {
    (async () => {
      try {
        let r = await apiJson('/api/groups/my');
        if (!r.ok) r = await apiJson('/api/groups');
        if (r.ok && Array.isArray(r.data)) {
          setGroups(r.data);
          if (r.data.length && !selectedGroup) setSelectedGroup(String(r.data[0].id));
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cache pay-to account per (group, period)
  const paytoCacheRef = useRef(new Map());
  const autoSelfPaidRef = useRef(new Set());
  const autoSelfPaidBusyRef = useRef(false);

  // Cache policy:
  // - Positive (account found): cache longer
  // - Negative (account missing/error): cache briefly only
  // This avoids "sticky" missing payout data after the winner links an account.
  const PAYTO_CACHE_OK_MS = 10 * 60 * 1000; // 10 minutes
  const PAYTO_CACHE_NEG_MS = 15 * 1000; // 15 seconds

  const fetchPayToForPeriod = useCallback(
    async (groupId, periodNumber, opts = {}) => {
      const key = `${groupId}:${periodNumber}`;
      const now = Date.now();

      const cached = paytoCacheRef.current.get(key);
      if (!opts.force && cached && typeof cached === 'object' && cached.exp && cached.exp > now) {
        return cached.value;
      }
      if (!opts.force && cached && !(cached && cached.exp)) {
        // Back-compat: old cache shape
        return cached;
      }

      paytoCacheRef.current.delete(key);

      try {
        // ✅ Backend expects period_number. Using the wrong param causes the API to return 400,
        // which then shows "Winner hasn't set payout account yet".
        const r = await apiJson(`/api/bank/payto?group_id=${groupId}&period_number=${periodNumber}`);
        if (r.ok && r.data?.account) {
          const v = {
            account: r.data.account,
            amount_due: r.data.amount_due ?? null,
            source: r.data.source ?? null,
          };
          paytoCacheRef.current.set(key, { value: v, exp: now + PAYTO_CACHE_OK_MS });
          return v;
        }
      } catch {
        // ignore
      }

      paytoCacheRef.current.set(key, { value: null, exp: now + PAYTO_CACHE_NEG_MS });
      return null;
    },
    [PAYTO_CACHE_OK_MS, PAYTO_CACHE_NEG_MS]
  );

  const clearPaytoCache = useCallback(() => {
    paytoCacheRef.current.clear();
  }, []);

  // Load payments table
  const loadTable = useCallback(
    async (groupId) => {
      if (!groupId) return;
      setLoadingTable(true);
      try {
        const pay = await apiJson(`/api/payments/${groupId}`);
        if (!pay.ok) {
          show(pay.text || 'Failed to load payments');
          setRows([]);
          setSummary({ paid: 0, pending: 0, late: 0 });
          return;
        }

        const baseRows = pay.data.rows || [];
        if (!baseRows.length) {
          setRows([]);
          setSummary({ paid: 0, pending: 0, late: 0 });
          return;
        }

        // Winner info is already attached to each payment row by the backend.
        // Build a stable winner map per period.
        const winnerByPeriod = new Map();
        for (const r of baseRows) {
          const p = Number(r.period_number);
          if (!p) continue;
          const cur = winnerByPeriod.get(p) || { winner_user_id: null, winner_membership_index: null };
          const wu = r.winner_user_id != null ? Number(r.winner_user_id) : cur.winner_user_id;
          const wmi =
            r.winner_membership_index != null ? Number(r.winner_membership_index) : cur.winner_membership_index;
          winnerByPeriod.set(p, { winner_user_id: wu, winner_membership_index: wmi });
        }

        // Fetch payout account once per period (only when I'm not the winner).
        const payoutByPeriod = new Map();
        await Promise.all(
          Array.from(winnerByPeriod.keys()).map(async (p) => {
            const w = winnerByPeriod.get(p);
            const winnerUserId = w?.winner_user_id;
            const myId = myUserId;

            if (myId == null) {
              payoutByPeriod.set(p, null);
              return;
            }

            // If winner is not decided yet, don't try to fetch pay-to.
            if (winnerUserId == null) {
              payoutByPeriod.set(p, null);
              return;
            }

            // If backend already knows the winner and it's me, skip (winner cannot fetch pay-to).
            if (winnerUserId != null && Number(winnerUserId) === Number(myId)) {
              payoutByPeriod.set(p, null);
              return;
            }

            const got = await fetchPayToForPeriod(groupId, p);
            payoutByPeriod.set(p, got?.account || null);
          })
        );

        const myId = myUserId;

        const enriched = baseRows.map((r) => {
          const p = Number(r.period_number);
          const w = winnerByPeriod.get(p) || { winner_user_id: null, winner_membership_index: null };
          const winnerUserId = w.winner_user_id != null ? Number(w.winner_user_id) : null;
          const winnerMIdx = w.winner_membership_index != null ? Number(w.winner_membership_index) : null;

          const mIdx = Number(r.membership_index || 1);

          const isWinnerSlot =
            myId != null &&
            winnerUserId != null &&
            Number(winnerUserId) === Number(myId) &&
            Number(winnerMIdx || 1) === Number(mIdx);

          // Same user as winner (e.g., user has multiple memberships and one won)
          // -> no transfer needed, but the payment row still exists for tracking.
          const isSelfTransfer =
            myId != null && winnerUserId != null && Number(winnerUserId) === Number(myId) && !isWinnerSlot;

          return {
            ...r,
            payout: payoutByPeriod.get(p) || null,
            winner_user_id: winnerUserId,
            winner_membership_index: winnerMIdx,
            is_winner_slot: isWinnerSlot,
            self_transfer: isSelfTransfer,
          };
        });

        const s = { paid: 0, pending: 0, late: 0 };
        for (const r of enriched) {
          // The My Payments counters should reflect actions the viewer must take.
          // 1) Winner slot: no payment action.
          if (r.is_winner_slot) continue;

          // 2) If the viewer is also the winner for this period (multiple memberships),
          // the "other membership" rows are self-transfer and will be auto-recorded.
          // Don't count them as pending/late for the viewer.
          if (
            myUserId != null &&
            r.winner_user_id != null &&
            Number(r.winner_user_id) === Number(myUserId)
          ) {
            continue;
          }

          if (r.status === 'paid') s.paid++;
          else if (r.status === 'late') s.late++;
          else s.pending++;
        }

        setSummary(s);
        setRows(enriched);
      } finally {
        setLoadingTable(false);
      }
    },
    [fetchPayToForPeriod, show, myUserId]
  );

  useEffect(() => {
    // Avoid stale "payout missing" after bank links change.
    clearPaytoCache();
    if (selectedGroup) loadTable(selectedGroup);
  }, [selectedGroup, loadTable, clearPaytoCache]);

  // realtime updates
  useEffect(() => {
    if (!selectedGroup) return;
    const gid = Number(selectedGroup);
    socket.emit('joinRoom', { groupId: gid });

    const onChanged = (payload) => {
      const pGid = payload?.groupId ?? payload?.group_id;
      if (Number(pGid) === gid) loadTable(gid);
    };

    socket.on('paymentStatusChanged', onChanged);
    return () => {
      socket.off('paymentStatusChanged', onChanged);
      socket.emit('leaveRoom', { groupId: gid });
    };
  }, [selectedGroup, loadTable]);


  // Auto-mark paid when the logged-in user is also the winner (self-transfer).
  // In this case there is no real "pay to" action needed, so we record it automatically.
  useEffect(() => {
    if (!selectedGroup) return;
    const gid = Number(selectedGroup);
    if (!gid || myUserId == null) return;
    if (autoSelfPaidBusyRef.current) return;

    const candidates = (rows || []).filter((r) => {
      if (!r) return false;
      if (Number(r.group_id) !== gid) return false;
      if (r.is_winner_slot) return false;
      if (r.status === 'paid') return false;
      const wu = r.winner_user_id != null ? Number(r.winner_user_id) : null;
      return wu != null && Number(wu) === Number(myUserId);
    });

    const todo = candidates.filter((r) => {
      const key = `${gid}:${Number(r.period_number)}:${Number(r.membership_index || 1)}`;
      return !autoSelfPaidRef.current.has(key);
    });

    if (!todo.length) return;

    autoSelfPaidBusyRef.current = true;

    (async () => {
      try {
        const results = await Promise.all(
          todo.map((r) => {
            const p = Number(r.period_number);
            const mi = Number(r.membership_index || 1);
            const key = `${gid}:${p}:${mi}`;
            autoSelfPaidRef.current.add(key);
            return apiJson(`/api/payments/${gid}/${p}/mark-paid?membershipIndex=${encodeURIComponent(mi)}`, {
              method: 'POST',
            });
          })
        );

        const failed = results.find((x) => !x?.ok);
        if (failed) {
          show(failed.text || 'Failed to auto-mark paid');
          return;
        }

        await loadTable(gid);
      } finally {
        autoSelfPaidBusyRef.current = false;
      }
    })();
  }, [rows, selectedGroup, myUserId, loadTable, show]);

  // Mark paid for all unpaid memberships in a period.
  // This supports users with multiple memberships while avoiding duplicate cards.
  const handleMarkPaidPeriod = async (items) => {
    if (!items?.length) return;
    const sample = items[0];
    const groupId = Number(sample.group_id);
    const periodNumber = Number(sample.period_number);

    const payable = items.filter((r) => !r.is_winner_slot && r.status !== 'paid');
    if (!payable.length) return;

    const membershipIndices = payable.map((r) => Number(r.membership_index || 1));

    const results = await Promise.all(
      membershipIndices.map((mi) =>
        apiJson(
          `/api/payments/${groupId}/${periodNumber}/mark-paid?membershipIndex=${encodeURIComponent(mi)}`,
          { method: 'POST' }
        )
      )
    );

    const failed = results.find((r) => !r.ok);
    if (failed) return show(failed.text || 'Failed to mark paid');

    show(membershipIndices.length > 1 ? 'Marked paid (all memberships)' : 'Marked paid');
    await loadTable(groupId);
  };

  if (loading) return <div className="main-content">Loading…</div>;

  const PeriodCard = ({ items }) => {
    const first = items?.[0];
    if (!first) return null;

    const periodNumber = Number(first.period_number);
    const isOrganiserBenefitPeriod =
      Number(selectedGroupObj?.organiser_benefit_period || 0) === Number(periodNumber);

    // NOTE: Some backend responses only attach winner fields on certain rows.
    // For the *logged-in* user, the most reliable signal that "I am the winner"
    // is whether this period contains a winner-slot row for me.
    const winnerSlot = items.find((r) => !!r.is_winner_slot) || null;
    const payable = items.filter((r) => !r.is_winner_slot);
    const unpaidPayable = payable.filter((r) => r.status !== 'paid');

    const isWinnerUser = !!winnerSlot;

    const payoutRow = payable.find((r) => r.payout) || null;
    const payout = payoutRow ? payoutRow.payout : null;

    // Winner may not be decided yet for future periods.
    // winner_user_id may be null for future periods. Treat 0/non-positive as "no winner".
    const hasWinner = payable.some((r) => Number(r.winner_user_id) > 0);

    const requiresTransfer = hasWinner && payable.some((r) => !r.self_transfer);
    const canProceed = hasWinner && (!requiresTransfer || !!payout);

    const totalAmountToPay = payable.reduce((sum, r) => {
      const v = Number(r.amount_due ?? r.base_contribution ?? 0);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);

    const status = (() => {
      // Winner view of the organiser benefit period should always read as organiser benefit,
      // even if there are other (self-transfer) memberships that get auto-recorded.
      if (isOrganiserBenefitPeriod && isWinnerUser) return 'organiser benefit';

      if (!payable.length) {
        return isOrganiserBenefitPeriod ? 'organiser benefit' : 'winner';
      }
      if (payable.every((r) => r.status === 'paid')) return 'paid';
      if (payable.some((r) => r.status === 'late')) return 'late';
      return 'pending';
    })();

    const showPaidAction = unpaidPayable.length > 0 && !isWinnerUser;

    return (
      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 12,
          marginBottom: 12,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <strong>Period</strong>
            <div>
              #{periodNumber}
              {items.length > 1 ? <span style={{ opacity: 0.7 }}> · {items.length} memberships</span> : null}
            </div>
          </div>
          <div>
            <strong>Status</strong>
            <div>{status}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 600 }}>Due (group)</div>
            <div>{fmtGroup(first.due_date, groupZone, 'ccc d LLL yyyy, t')}</div>
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>Due (your time)</div>
            <div>{fmtLocal(first.due_date, 'ccc d LLL yyyy, t')}</div>
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 600 }}>Amount to pay</div>
          <div>
            {!payable.length || totalAmountToPay <= 0
              ? '—'
              : isWinnerUser
              ? `${money(totalAmountToPay, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} (auto-recorded)`
              : money(totalAmountToPay, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 600 }}>Pay to (Winner)</div>
          {winnerSlot && !payable.length ? (
            <div style={{ opacity: 0.7 }}>
              {isOrganiserBenefitPeriod ? 'You receive this period (organiser benefit)' : 'You are the winner'}
            </div>
          ) : winnerSlot && payable.length ? (
            <div style={{ opacity: 0.7 }}>
              {isOrganiserBenefitPeriod ? 'Winner: You (organiser benefit).' : 'Winner: You.'}
              <div style={{ marginTop: 4 }}>
                For your other memberships, no transfer is needed — it will be recorded as paid automatically.
              </div>
            </div>
          ) : !requiresTransfer ? (
            <div style={{ opacity: 0.7 }}>No transfer needed (same user as winner)</div>
          ) : payout ? (
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong>{payout.account_holder}</strong>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => copyPayoutField(payout, 'name')}
                  title="Copy name"
                  aria-label="Copy name"
                >
                  <FaRegCopy />
                </button>
              </div>

              <div>
                BSB: <code>{displayBankValue(payout.bsb, 'bsb')}</code>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 6, cursor: 'pointer' }}
                  onClick={() => copyPayoutField(payout, 'bsb')}
                  title="Copy BSB"
                  aria-label="Copy BSB"
                >
                  <FaRegCopy />
                </button>
              </div>

              <div>
                Account: <code>{displayBankValue(payout.account_number, 'account')}</code>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 6, cursor: 'pointer' }}
                  onClick={() => copyPayoutField(payout, 'account')}
                  title="Copy account"
                  aria-label="Copy account"
                >
                  <FaRegCopy />
                </button>
              </div>
            </div>
          ) : (
            <div style={{ opacity: 0.7 }}>{hasWinner ? 'Winner hasn’t set payout account yet' : 'Winner not decided yet'}</div>
          )}
        </div>

        <div style={{ marginTop: 8, textAlign: 'right' }}>
          {showPaidAction ? (
            <button
              className="btn btn-primary"
              onClick={() => handleMarkPaidPeriod(items)}
              disabled={!canProceed}
              title={!canProceed ? 'Payout not set yet' : 'Mark paid'}
            >
              Paid
            </button>
          ) : (
            <span style={{ opacity: 0.7 }}>—</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="main-content">
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <img src={logo} alt="ChitConnect" className="login-logo" />
      </div>

      {message && <div className="toast-message">{message}</div>}

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0 }}>My Payments</h2>
          {selectedGroup && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                clearPaytoCache();
                loadTable(selectedGroup);
              }}
              disabled={loadingTable}
            >
              Refresh
            </button>
          )}
        </div>

        <label style={{ display: 'block', marginTop: 10, marginBottom: 8 }}>Select Group</label>
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className="styled-dropdown styled-dropdown-highlight"
          style={{ maxWidth: 360 }}
        >
          <option value="">— choose —</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>

        {selectedGroup && (
          <>
            <div style={{ marginTop: 10, fontSize: 14 }}>
              <strong>Group time:</strong> {tzLabel(groupZone)} &nbsp;•&nbsp;
              <strong>Your time:</strong> {tzLabel()}
            </div>

            {/* Desktop/tablet: table; Mobile: cards */}
            {!isMobile ? (
              <div style={{ marginTop: 16 }}>
                <table className="styled-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Due (group)</th>
                      <th>Due (your time)</th>
                      <th>Amount</th>
                      <th>Pay to (Winner)</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingTable && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', opacity: 0.6 }}>
                          Loading…
                        </td>
                      </tr>
                    )}

                    {!loadingTable &&
                      periodGroups.map(({ period, items }) => {
                        const first = items?.[0];
                        if (!first) return null;

                        const isOrganiserBenefitPeriod =
                          Number(selectedGroupObj?.organiser_benefit_period || 0) === Number(period);
                        const winnerSlot = items.find((r) => !!r.is_winner_slot) || null;

                        const payable = items.filter((r) => !r.is_winner_slot);
                        const unpaidPayable = payable.filter((r) => r.status !== 'paid');

                        // Same reasoning as mobile cards: the most reliable signal that
                        // "I am the winner" (for this viewer) is the presence of a winner-slot row.
                        const isWinnerUser = !!winnerSlot;


                        const payoutRow = payable.find((r) => r.payout) || null;
                        const payout = payoutRow ? payoutRow.payout : null;

                        const requiresTransfer = payable.some((r) => !r.self_transfer);
                        const canProceed = !requiresTransfer || !!payout;

                        const totalAmountToPay = payable.reduce((sum, r) => {
                          const v = Number(r.amount_due ?? r.base_contribution ?? 0);
                          return sum + (Number.isFinite(v) ? v : 0);
                        }, 0);

                        const status = (() => {
                          if (isOrganiserBenefitPeriod && isWinnerUser) return 'organiser benefit';
                          if (!payable.length) return isOrganiserBenefitPeriod ? 'organiser benefit' : 'winner';
                          if (payable.every((r) => r.status === 'paid')) return 'paid';
                          if (payable.some((r) => r.status === 'late')) return 'late';
                          return 'pending';
                        })();

                        const showPaidAction = unpaidPayable.length > 0 && !isWinnerUser;

                        return (
                          <tr key={`p-${period}`}>
                            <td>
                              #{period}
                              {items.length > 1 ? <span style={{ opacity: 0.7 }}> ({items.length} memberships)</span> : null}
                            </td>
                            <td>{fmtGroup(first.due_date, groupZone, 'ccc d LLL yyyy, t')}</td>
                            <td>{fmtLocal(first.due_date, 'ccc d LLL yyyy, t')}</td>

                            <td>
                              {!payable.length || totalAmountToPay <= 0 ? (
                                <span style={{ opacity: 0.7 }}>—</span>
                              ) : (
                                <>
                                  {money(totalAmountToPay, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                  {isWinnerUser ? <span style={{ opacity: 0.7 }}> (auto-recorded)</span> : null}
                                </>
                              )}
                            </td>

                            <td>
                              {winnerSlot && !payable.length ? (
                                <span style={{ opacity: 0.7 }}>
                                  {isOrganiserBenefitPeriod ? 'You receive (organiser benefit)' : 'You are the winner'}
                                </span>
                              ) : winnerSlot && payable.length ? (
                                <span style={{ opacity: 0.7 }}>
                                  {isOrganiserBenefitPeriod ? 'Winner: You (organiser benefit). ' : 'Winner: You. '}
                                  No transfer needed — it will be recorded automatically.
                                </span>
                              ) : !requiresTransfer ? (
                                <span style={{ opacity: 0.7 }}>No transfer needed (same user as winner)</span>
                              ) : payout ? (
                                <div style={{ display: 'grid', gap: 4 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <strong>{payout.account_holder}</strong>
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-sm"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => copyPayoutField(payout, 'name')}
                                      title="Copy name"
                                      aria-label="Copy name"
                                    >
                                      <FaRegCopy />
                                    </button>
                                  </div>
                                  <div>
                                    BSB:&nbsp;<code>{displayBankValue(payout.bsb, 'bsb')}</code>
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-sm"
                                      style={{ marginLeft: 6, cursor: 'pointer' }}
                                      onClick={() => copyPayoutField(payout, 'bsb')}
                                      title="Copy BSB"
                                      aria-label="Copy BSB"
                                    >
                                      <FaRegCopy />
                                    </button>
                                  </div>
                                  <div>
                                    Account:&nbsp;<code>{displayBankValue(payout.account_number, 'account')}</code>
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-sm"
                                      style={{ marginLeft: 6, cursor: 'pointer' }}
                                      onClick={() => copyPayoutField(payout, 'account')}
                                      title="Copy account"
                                      aria-label="Copy account"
                                    >
                                      <FaRegCopy />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <span style={{ opacity: 0.7 }}>Winner hasn’t set payout account yet</span>
                              )}
                            </td>

                            <td>{status}</td>

                            <td>
                              {showPaidAction ? (
                                <button
                                  className="btn btn-primary"
                                  onClick={() => handleMarkPaidPeriod(items)}
                                  disabled={!canProceed}
                                  title={!canProceed ? 'Payout not set yet' : 'Mark paid'}
                                >
                                  Paid
                                </button>
                              ) : (
                                <span style={{ opacity: 0.7 }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}

                    {!loadingTable && !periodGroups.length && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', opacity: 0.6 }}>
                          No payments to show.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ marginTop: 16 }}>
                {loadingTable && <div style={{ textAlign: 'center', opacity: 0.6 }}>Loading…</div>}
                {!loadingTable && periodGroups.map((g) => <PeriodCard key={`pc-${g.period}`} items={g.items} />)}
                {!loadingTable && !periodGroups.length && (
                  <div style={{ textAlign: 'center', opacity: 0.6 }}>No payments to show.</div>
                )}
              </div>
            )}

            <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                ✅ Paid: <strong>{summary.paid}</strong>
              </div>
              <div>
                ⏳ Pending: <strong>{summary.pending}</strong>
              </div>
              <div>
                ⚠ Late: <strong>{summary.late}</strong>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
